import React from 'react';
import { MdDeliveryDining } from 'react-icons/md';
import { FormData, FormErrors } from '../../hooks/useCheckoutForm';
import { AddressAutocompleteInput, PlaceResult } from './AddressAutocompleteInput';

interface DeliveryFormProps {
  formData: FormData;
  errors: FormErrors;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onContinue: () => void;
  quoteLoading: boolean;
  cartItemsCount: number;
}

export const DeliveryForm: React.FC<DeliveryFormProps> = ({
  formData,
  errors,
  handleChange,
  onContinue,
  quoteLoading,
  cartItemsCount,
}) => {
  // Automatically parse city, state, and ZIP code from selected Google Places result
  const handlePlaceSelected = (place: PlaceResult) => {
    if (!place.address) return;

    // Example address format: "300 Bunn Dr, Princeton, NJ 08540, USA"
    const parts = place.address.split(',').map((p) => p.trim());
    
    if (parts.length >= 3) {
      const city = parts[parts.length - 3]; // e.g., "Princeton"
      const stateZipPart = parts[parts.length - 2]; // e.g., "NJ 08540"
      const stateZipSplit = stateZipPart.split(' ');
      
      const state = stateZipSplit[0]; // e.g., "NJ"
      const zip = stateZipSplit[1];   // e.g., "08540"

      // Trigger form handlers to update city, state, and postal code fields automatically
      handleChange({
        target: { name: 'locality', value: city }
      } as React.ChangeEvent<HTMLInputElement>);

      handleChange({
        target: { name: 'administrative_district_level_1', value: state }
      } as React.ChangeEvent<HTMLInputElement>);

      if (zip) {
        handleChange({
          target: { name: 'postal_code', value: zip }
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onContinue();
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      {/* Personal Information - 2 Column Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your full name"
            aria-describedby={errors.name ? 'name-error' : undefined}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p id="name-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone.startsWith('+1 ') ? formData.phone : `+1 ${formData.phone}`}
            onChange={(e) => {
              let value = e.target.value;
              if (!value.startsWith('+1 ')) {
                value = '+1 ' + value.replace(/^\+1\s*/, '');
              }
              handleChange({
                ...e,
                target: { ...e.target, name: 'phone', value }
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            onKeyDown={(e) => {
              const input = e.currentTarget;
              if ((e.key === 'Backspace' || e.key === 'Delete') && input.selectionStart !== null && input.selectionStart <= 3) {
                e.preventDefault();
              }
            }}
            onClick={(e) => {
              const input = e.currentTarget;
              if (input.selectionStart !== null && input.selectionStart < 3) {
                input.setSelectionRange(3, 3);
              }
            }}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="+1 Enter your phone number"
            aria-describedby={errors.phone ? 'phone-error' : undefined}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p id="phone-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.phone}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address (Optional)
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter your email address (optional)"
          aria-describedby={errors.email ? 'email-error' : undefined}
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p id="email-error" className="mt-1 text-sm text-red-500" role="alert">
            {errors.email}
          </p>
        )}
      </div>

      {/* Delivery Address Fields with Google Places Autocomplete */}
      <AddressAutocompleteInput
        id="address_line_1"
        name="address_line_1"
        label="Street Address"
        value={formData.address_line_1 || ''}
        onChange={handleChange}
        onSelectPlace={handlePlaceSelected}
        error={errors.address_line_1}
        placeholder="Start typing your street address..."
        required={true}
      />

      {/* State and City */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="administrative_district_level_1" className="block text-sm font-medium text-gray-700 mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="administrative_district_level_1"
            name="administrative_district_level_1"
            value={formData.administrative_district_level_1 || ''}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.administrative_district_level_1 ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter state"
            aria-describedby={errors.administrative_district_level_1 ? 'state-error' : undefined}
            aria-invalid={!!errors.administrative_district_level_1}
          />
          {errors.administrative_district_level_1 && (
            <p id="state-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.administrative_district_level_1}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="locality" className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="locality"
            name="locality"
            value={formData.locality || ''}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.locality ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter city"
            aria-describedby={errors.locality ? 'city-error' : undefined}
            aria-invalid={!!errors.locality}
          />
          {errors.locality && (
            <p id="city-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.locality}
            </p>
          )}
        </div>
      </div>

      {/* ZIP Code */}
      <div>
        <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
          ZIP Code <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="postal_code"
          name="postal_code"
          value={formData.postal_code || ''}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
            errors.postal_code ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="ZIP Code"
          aria-describedby={errors.postal_code ? 'zip-error' : undefined}
          aria-invalid={!!errors.postal_code}
        />
        {errors.postal_code && (
          <p id="zip-error" className="mt-1 text-sm text-red-500" role="alert">
            {errors.postal_code}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-1">
          Special Requests (Optional)
        </label>
        <textarea
          id="specialRequests"
          name="specialRequests"
          value={formData.specialRequests}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900"
          placeholder="Any special instructions for your delivery"
        />
      </div>

      {/* Continue Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={quoteLoading || cartItemsCount === 0}
          className={`w-full bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center ${
            (quoteLoading || cartItemsCount === 0) ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {quoteLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" aria-hidden="true"></div>
          ) : (
            <MdDeliveryDining className="w-5 h-5 mr-2" aria-hidden="true" />
          )}
          {quoteLoading ? 'Getting Quote...' : 'Continue'}
        </button>
      </div>
    </form>
  );
};